import { ValueAdapter } from './../value.adapter';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { Model } from 'app/model/backendmodel';
import { ActivityAsyncAdapter } from './activity.adapter';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';

import { Dataset } from 'app/model/dataset/dataset';
import { LookupActivity } from 'app/model/pipeline/lookup.activity';
import { convertParametersToModel, fillParameterValuesForResource } from 'app/shared/utils/parameter-converter.util';
import _ from 'lodash';
import { CopySource } from '../../pipeline/copysource/copysource';
import { generateUUID } from 'app/shared/utils/uuid.util';
import { isLogicAppExpression, Owned, TabularCopySource } from 'shell-models-lib';
import {
    ActivityConnectionContext,
    IDatabaseConnectionContext,
    ILookupActivityContext
} from 'app/interfaces/copilot.pipeline.action.types';
import {
    findLinkedServiceFromContext,
    setDbFromLs,
    setSourceDataset
} from 'app/shared/utils/update-pipeline-skill.util';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';
import { createDatasetWithLinkedService } from 'app/authoring/component/activities/activity-inline-dataset/utils';
import { ModelService } from 'app/model/services/model.service';
import {
    getAllowedFileFormatsFromDatasetConfig,
    getSupportedLookupDatasetConfig
} from 'app/authoring/component/activities/shared/activitysource-tab/utils';
import { IFrameService } from 'app/services/iframe.service';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(LookupActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        policy: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            firstRowOnly: '*',
            dataset: '*',
            datasetSettings: '*',
            source: '*'
        }
    }
})
export class LookupActivityAdapter extends ActivityAsyncAdapter<
    LookupActivity,
    Model.LookupActivity,
    ILookupActivityContext
> {
    public model(): IModel {
        return Metamodel.modelFor(LookupActivity);
    }

    public override async postToModel(activity: LookupActivity): Promise<void> {
        await super.postToModel(activity);

        if (this.context.adapterConfig.inlineDataset) {
            if (activity.sourceDataset) {
                const sourceDatasetModel = Metamodel.modelOf(activity.sourceDataset);
                if (sourceDatasetModel) {
                    const sourceDatasetAdapter = this.context.asyncAdapterFor(sourceDatasetModel);
                    if (sourceDatasetAdapter) {
                        await sourceDatasetAdapter.postToModel(activity.sourceDataset);
                    }
                }
            }
        }
    }

    public override async toFabricCopilotContext(activity: LookupActivity): Promise<ILookupActivityContext> {
        if (activity) {
            const activityContext: ILookupActivityContext = await super.toFabricCopilotContext(activity);
            if (activity.sourceDataset) {
                activityContext.Connection = await this.getDatasetCopilotContext(activity.sourceDataset);
                if (
                    activity.source instanceof TabularCopySource &&
                    activity.source.mode === 'query' &&
                    activityContext.Connection
                ) {
                    activityContext.Connection = {
                        ...activityContext.Connection,
                        Query: ValueAdapter.toResource(activity.source, TabularCopySource.READER_QUERY, true)
                    };
                }
            }
            activityContext.FirstRowOnly = ValueAdapter.toResource(
                activity,
                LookupActivity.FIRST_ROW_ONLY,
                true
            )?.toString();
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: ILookupActivityContext,
        model?: LookupActivity
    ): Promise<LookupActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.Connection) {
                const modelService = await getServiceInstance<ModelService>(this.context.contextData, 'ModelService');
                const iframeService = await getServiceInstance<IFrameService>(
                    this.context.contextData,
                    'IFrameService'
                );
                const linkedServiceInfo = await findLinkedServiceFromContext(context.Connection, {
                    modelService,
                    iframeService
                });
                if (linkedServiceInfo) {
                    const datasetRegistry = await getServiceInstance<DatasetRegistryService>(
                        this.context.contextData,
                        'DatasetRegistryService'
                    );
                    const linkedServiceRegistry = await getServiceInstance<LinkedServiceRegistryService>(
                        this.context.contextData,
                        'LinkedServiceRegistryService'
                    );
                    const { dataset } = createDatasetWithLinkedService(
                        linkedServiceInfo.linkedService,
                        linkedServiceInfo.linkedServiceType,
                        model,
                        { linkedServiceRegistry, datasetRegistry },
                        {
                            rootFolder: context.Connection.Type === 'LakehouseFiles' ? 'Files' : 'Tables',
                            supportedFileFormats: getAllowedFileFormatsFromDatasetConfig(
                                getSupportedLookupDatasetConfig({
                                    datasetRegistry
                                })
                            )
                        }
                    );
                    if (dataset) {
                        dataset.customLinkedServiceType = linkedServiceInfo.linkedServiceType;
                        setSourceDataset(model, dataset, { linkedServiceRegistry });
                        const sourceDatasetAdapter = this.context.asyncAdapterFor<
                            Dataset,
                            Model.Dataset,
                            ActivityConnectionContext
                        >(Metamodel.modelOf(dataset));
                        model.sourceDataset = await sourceDatasetAdapter.fromFabricCopilotContext(
                            context.Connection,
                            model.sourceDataset
                        );
                        if (
                            (context.Connection as IDatabaseConnectionContext).Query &&
                            model.source instanceof TabularCopySource
                        ) {
                            model.source.mode = 'query';
                            model.source.readerQuery = (context.Connection as IDatabaseConnectionContext).Query;
                        }
                        // post update the database from linked service
                        setDbFromLs(model.sourceDataset, model.sourceDataset.linkedService);
                    }
                }
            }
            let firstRowOnlyValue: string | boolean = context.FirstRowOnly;
            if (!isLogicAppExpression(firstRowOnlyValue)) {
                firstRowOnlyValue = firstRowOnlyValue?.toLowerCase() === 'true' ? true : false;
            }
            ValueAdapter.toModel(model, LookupActivity.FIRST_ROW_ONLY, firstRowOnlyValue);
        }
        return model;
    }

    protected async _toResource(activity: LookupActivity): Promise<Model.LookupActivity> {
        const modelLookupActivity: Model.LookupActivity = super._getCommonActivityProperties(
            activity,
            'Lookup'
        ) as Model.LookupActivity;
        modelLookupActivity.typeProperties = modelLookupActivity.typeProperties || {
            source: undefined,
            dataset: undefined
        };
        modelLookupActivity.typeProperties.firstRowOnly = ValueAdapter.toResource(
            activity,
            LookupActivity.FIRST_ROW_ONLY
        );

        if (activity.sourceDataset) {
            if (this.context.adapterConfig.inlineDataset || this.context.adapterConfig.convertToFabric) {
                const datasetModel = Metamodel.modelOf(activity.sourceDataset);
                const datasetAdapter = this.context.asyncAdapterFor(datasetModel);
                if (datasetAdapter) {
                    modelLookupActivity.typeProperties.datasetSettings = (
                        await datasetAdapter.toResource(activity.sourceDataset)
                    ).properties;
                } else {
                    this._logError('Cannot find dataset adapter in LookupActivityAdapter', {
                        datasetName: activity.sourceDataset.name,
                        datasetType: datasetModel?.name,
                    });
                }

            } else {
                modelLookupActivity.typeProperties.dataset = this.getDatasetReference(activity);
            }
        }

        if (activity.source) {
            const sourceAdapter = this.context.asyncAdapterFor(Metamodel.modelOf(activity.source));
            modelLookupActivity.typeProperties.source = sourceAdapter
                ? await sourceAdapter.toResource(activity.source)
                : undefined;
        }
        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelLookupActivity, parsed);
        }
        return modelLookupActivity;
    }

    protected async _toModel(modelActivity: Model.LookupActivity): Promise<LookupActivity> {
        const rawObject = {};
        const activity: LookupActivity = LookupActivity.MODEL.create<LookupActivity>(modelActivity.name);
        this._updatedCommonProperties(modelActivity, activity);
        modelActivity.typeProperties = modelActivity.typeProperties || { source: undefined, dataset: undefined };
        ValueAdapter.toModel(activity, LookupActivity.FIRST_ROW_ONLY, modelActivity.typeProperties.firstRowOnly);

        if (modelActivity.typeProperties.dataset) {
            activity.sourceDataset = ReferenceAdapter.toProxyModel<Dataset>(
                Metamodel.modelFor(Dataset),
                modelActivity.typeProperties.dataset,
                this.context
            );
            if (modelActivity.typeProperties.dataset.parameters) {
                convertParametersToModel(
                    activity,
                    !!this.context.adapterConfig.replaceIdentitiesForNonConcepts,
                    activity.sourceDataset,
                    activity.sourceDatasetParameters,
                    modelActivity.typeProperties.dataset.parameters
                );
            }
        } else if (modelActivity.typeProperties.datasetSettings) {
            const datasetSettings = modelActivity.typeProperties.datasetSettings;
            const datasetModel = Metamodel.modelByName(datasetSettings.type);
            const datasetAdapter = this.context.asyncAdapterFor(datasetModel);
            const dataset = Owned.create<Dataset>(datasetModel, activity);
            await datasetAdapter.toModel(
                { name: generateUUID().replace(/-/g, '_'), properties: datasetSettings },
                dataset
            );
            activity.sourceDataset = dataset;

            if (this._shouldGetArtifactDMTSConnectionFromSolutionTemplate()) {
                const dmtsCredentialKey = `${modelActivity.name}/Lookup`;
                const dmtsCredentialForArtifact = this.context.adapterConfig.artifactDMTSCredentials.get(dmtsCredentialKey);
                if (dmtsCredentialForArtifact != null) {
                    activity.sourceDataset.dmtsCredentialForArtifact = dmtsCredentialForArtifact;
                }
            }
        }

        if (modelActivity.typeProperties.source && modelActivity.typeProperties.source.type) {
            const sourceModel = Metamodel.modelByName(modelActivity.typeProperties.source.type);
            const sourceAdapter = this.context.asyncAdapterFor(sourceModel);
            activity.source = sourceAdapter
                ? await sourceAdapter.toModel(modelActivity.typeProperties.source, activity)
                : undefined;
            if (!(activity.source instanceof CopySource)) {
                throw new Error(
                    `Dataset source ${modelActivity.typeProperties.source.type} for ${activity.name} is not a valid dataset source`
                );
            }
            _.merge(rawObject, {
                typeProperties: {
                    source: await ValueAdapter.remainder(this.context, modelActivity.typeProperties.source, sourceModel)
                }
            });
        }

        activity.raw = JSON.stringify(
            _.merge(rawObject, await ValueAdapter.remainder(this.context, modelActivity, LookupActivity.MODEL))
        );
        return activity;
    }

    private getDatasetReference(activity: LookupActivity): Model.DatasetReference {
        const datasetReference = ReferenceAdapter.toResource(
            activity.sourceDataset as Dataset,
            this.context
        ) as Model.DatasetReference;
        datasetReference.parameters = fillParameterValuesForResource(
            activity.sourceDatasetParameters,
            this.context,
            this.ensureLogger()
        );
        return datasetReference;
    }
}
